import { authStorage } from './auth-storage'
import { toast } from 'sonner'

import { trackActionChain } from '@/api/mutations/action-chain'
import type {
  FinalAction,
  TrackActionChainRequest,
} from '@/api/schemas/action-chain'

const CHAIN_KEY = 'tm_action_chain_id'
const SESSION_KEY = 'tm_action_session_id'

type TrackedStepState = {
  chainId: string | null
  hasSource: boolean
  hasView: boolean
}

let trackedStepState: TrackedStepState = {
  chainId: null,
  hasSource: false,
  hasView: false,
}

export interface SourceInfo {
  productId?: number | string
  sourceImageUrl?: string
  sourceCategoryId?: number
  sourceCategoryCode?: string
  sourceCategoryName?: string
}

export interface ViewInfo {
  productId?: number | string
  sourceImageUrl?: string
  viewProductId?: number | string
  viewCategoryId?: number
  viewCategoryCode?: string
  viewCategoryName?: string
  viewReferenceUrl?: string
  viewIsOther?: boolean
}

let lastSourceInfo: SourceInfo | null = null
let lastViewInfo: ViewInfo | null = null
let deliveryQueue: Promise<void> = Promise.resolve()

const isSameSource = (a: SourceInfo, b: SourceInfo): boolean =>
  a.productId !== undefined && b.productId !== undefined
    ? String(a.productId) === String(b.productId)
    : Boolean(a.sourceImageUrl && a.sourceImageUrl === b.sourceImageUrl)

const generateId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`
}

const ensureChainId = (): string => {
  let id = sessionStorage.getItem(CHAIN_KEY)
  if (!id) {
    id = generateId()
    sessionStorage.setItem(CHAIN_KEY, id)
  }
  return id
}

const ensureSessionId = (): string => {
  let id = sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = generateId()
    sessionStorage.setItem(SESSION_KEY, id)
  }
  return id
}

const resetTrackedStepState = (chainId: string | null): void => {
  trackedStepState = {
    chainId,
    hasSource: false,
    hasView: false,
  }
}

export const resetTracking = (): void => {
  const chainId = generateId()
  sessionStorage.setItem(CHAIN_KEY, chainId)
  sessionStorage.setItem(SESSION_KEY, generateId())
  resetTrackedStepState(chainId)
  lastSourceInfo = null
  lastViewInfo = null
}

export const clearTracking = (): void => {
  sessionStorage.removeItem(CHAIN_KEY)
  sessionStorage.removeItem(SESSION_KEY)
  resetTrackedStepState(null)
  lastSourceInfo = null
  lastViewInfo = null
}

export const startNewChain = (): string => {
  const id = generateId()
  sessionStorage.setItem(CHAIN_KEY, id)
  resetTrackedStepState(id)
  return id
}

const isTrackingEnabled = (): boolean => Boolean(authStorage.getAccess())

const send = (payload: TrackActionChainRequest): void => {
  if (!isTrackingEnabled()) return
  // The server updates one row per chainId. Preserve the order of SOURCE,
  // VIEW and ACTION even when their HTTP requests have different latencies.
  deliveryQueue = deliveryQueue.catch(() => undefined).then(async () => {
    if (!isTrackingEnabled()) return
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await trackActionChain(payload)
        return
      } catch {
        if (attempt < 2) {
          await new Promise((resolve) => window.setTimeout(resolve, 400 * (attempt + 1)))
        }
      }
    }
    toast.error('操作记录同步失败，请检查网络后重新操作')
  })
}

const syncTrackedStepState = (chainId: string): void => {
  if (trackedStepState.chainId === chainId) return
  resetTrackedStepState(chainId)
}

const isFreshChain = (): boolean =>
  !trackedStepState.hasSource && !trackedStepState.hasView

const sendSourcePayload = (
  chainId: string,
  sessionId: string,
  info: SourceInfo,
): void => {
  send({
    chainId,
    sessionId,
    productId: info.productId,
    sourceImageUrl: info.sourceImageUrl,
    stepType: 'SOURCE',
    sourceCategoryId: info.sourceCategoryId,
    sourceCategoryCode: info.sourceCategoryCode,
    sourceCategoryName: info.sourceCategoryName,
  })
  trackedStepState.hasSource = true
}

const sendViewPayload = (
  chainId: string,
  sessionId: string,
  info: ViewInfo,
): void => {
  send({
    chainId,
    sessionId,
    productId: info.productId,
    sourceImageUrl: info.sourceImageUrl,
    stepType: 'VIEW',
    viewProductId: info.viewProductId,
    viewCategoryId: info.viewCategoryId,
    viewCategoryCode: info.viewCategoryCode,
    viewCategoryName: info.viewCategoryName,
    viewReferenceUrl: info.viewReferenceUrl,
    viewIsOther: info.viewIsOther,
  })
  trackedStepState.hasView = true
}

export const trackSource = (info: SourceInfo): void => {
  const chainId = ensureChainId()
  const sessionId = ensureSessionId()
  syncTrackedStepState(chainId)

  sendSourcePayload(chainId, sessionId, info)
  lastSourceInfo = info
}

export const trackView = (info: ViewInfo): void => {
  const chainId = ensureChainId()
  const sessionId = ensureSessionId()
  syncTrackedStepState(chainId)

  const shouldInheritSource =
    isFreshChain() &&
    lastSourceInfo !== null &&
    isSameSource(lastSourceInfo, info)

  if (shouldInheritSource && lastSourceInfo) {
    sendSourcePayload(chainId, sessionId, {
      ...lastSourceInfo,
      productId: info.productId,
      sourceImageUrl: info.sourceImageUrl,
    })
  }

  sendViewPayload(chainId, sessionId, info)
  lastViewInfo = info
}

export const trackAction = (
  finalAction: FinalAction,
  source: SourceInfo,
  resultImageUrl?: string,
): void => {
  const chainId = ensureChainId()
  const sessionId = ensureSessionId()
  syncTrackedStepState(chainId)

  // A final action starts a fresh chain. If the user downloads the same result
  // again without reselecting either image, carry the current choices forward.
  if (
    isFreshChain() &&
    lastSourceInfo &&
    isSameSource(lastSourceInfo, source)
  ) {
    sendSourcePayload(chainId, sessionId, {
      ...lastSourceInfo,
      productId: source.productId,
      sourceImageUrl: source.sourceImageUrl,
    })
  }
  if (
    !trackedStepState.hasView &&
    lastViewInfo &&
    isSameSource(lastViewInfo, source)
  ) {
    sendViewPayload(chainId, sessionId, {
      ...lastViewInfo,
      productId: source.productId,
      sourceImageUrl: source.sourceImageUrl,
    })
  }
  send({
    chainId,
    sessionId,
    productId: source.productId,
    sourceImageUrl: source.sourceImageUrl,
    stepType: 'ACTION',
    finalAction,
    resultImageUrl,
  })
  startNewChain()
}
